import React, { useState, useMemo, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { PlusIcon, EditIcon, TrashIcon, CheckIcon, XIcon, ChevronIcon } from "../icons";
import S from "../styles";

const API_URL = process.env.REACT_APP_API_URL || "https://api.nycmanufacturing.com";

const CATEGORIES = ["3D Printers", "Lasers", "CNC Machines", "Injection Molders", "UV Printers"];

const MAINTENANCE_TYPES = [
  "Preventive",
  "Repair",
  "Calibration",
  "Cleaning",
  "Inspection",
  "Part Replacement",
];

const CATEGORY_COLORS = {
  "3D Printers": "#8b5cf6",
  "Lasers": "#ef4444",
  "CNC Machines": "#3b82f6",
  "Injection Molders": "#f59e0b",
  "UV Printers": "#10b981",
};

const MAINT_TYPE_COLORS = {
  Preventive: "#3b82f6",
  Repair: "#ef4444",
  Calibration: "#f59e0b",
  Cleaning: "#10b981",
  Inspection: "#8b5cf6",
  "Part Replacement": "#0033FF",
};

const FULL_MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

function DatePicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const dropRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  // Parse value (YYYY-MM-DD) into parts
  const parsed = value ? { y: +value.slice(0, 4), m: +value.slice(5, 7), d: +value.slice(8, 10) } : null;
  const [selYear, setSelYear] = useState(parsed ? parsed.y : new Date().getFullYear());
  const [selMonth, setSelMonth] = useState(parsed ? parsed.m : new Date().getMonth() + 1);
  const [selDay, setSelDay] = useState(parsed ? parsed.d : null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      if (dropRef.current && dropRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Reset picker state when opening
  const handleOpen = () => {
    if (parsed) { setSelYear(parsed.y); setSelMonth(parsed.m); setSelDay(parsed.d); }
    else { setSelYear(new Date().getFullYear()); setSelMonth(new Date().getMonth() + 1); setSelDay(null); }
    // Position the dropdown relative to the button
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, left: rect.left });
    }
    setOpen(true);
  };

  const daysInMonth = new Date(selYear, selMonth, 0).getDate();
  const firstDow = new Date(selYear, selMonth - 1, 1).getDay();
  const days = [];
  for (let i = 0; i < firstDow; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) days.push(d);

  const selectDay = (d) => {
    setSelDay(d);
    const str = `${selYear}-${String(selMonth).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    onChange(str);
    setOpen(false);
  };

  const clearDate = (e) => {
    e.stopPropagation();
    onChange("");
    setOpen(false);
  };

  const displayText = value
    ? new Date(value + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : "";

  const years = [];
  for (let y = 2018; y <= new Date().getFullYear() + 1; y++) years.push(y);

  return (
    <div>
      <button
        ref={btnRef}
        type="button"
        onClick={handleOpen}
        style={{
          ...S.cellInput,
          fontSize: 14,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "#F4F2EC",
          minHeight: 32,
          textAlign: "left",
          color: value ? "#0A0A0F" : "rgba(10,10,15,0.58)",
          fontFamily: value ? "'JetBrains Mono', monospace" : "'Archivo', sans-serif",
        }}
      >
        <span>{displayText || "Select date"}</span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(10,10,15,0.58)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      </button>
      {open && ReactDOM.createPortal(
        <div ref={dropRef} style={{
          position: "fixed",
          top: pos.top,
          left: pos.left,
          zIndex: 9999,
          background: "#FFFFFF",
          border: "2px solid #0A0A0F",
          borderRadius: 0,
          padding: 14,
          boxShadow: "0 12px 32px rgba(0,0,0,.5)",
          minWidth: 260,
        }}>
          {/* Year / Month nav */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
            <button type="button" onClick={() => { if (selMonth === 1) { setSelMonth(12); setSelYear(y => y - 1); } else setSelMonth(m => m - 1); }}
              style={{ background: "none", border: "2px solid #0A0A0F", borderRadius: 0, padding: "3px 8px", cursor: "pointer", color: "rgba(10,10,15,0.58)", fontSize: 14 }}>‹</button>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <select value={selMonth} onChange={(e) => setSelMonth(+e.target.value)}
                style={{ background: "#F4F2EC", border: "2px solid #0A0A0F", borderRadius: 0, color: "#0A0A0F", fontSize: 13, padding: "3px 6px", fontFamily: "'Archivo', sans-serif", outline: "none", cursor: "pointer" }}>
                {FULL_MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
              </select>
              <select value={selYear} onChange={(e) => setSelYear(+e.target.value)}
                style={{ background: "#F4F2EC", border: "2px solid #0A0A0F", borderRadius: 0, color: "#0A0A0F", fontSize: 13, padding: "3px 6px", fontFamily: "'JetBrains Mono', monospace", outline: "none", cursor: "pointer" }}>
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <button type="button" onClick={() => { if (selMonth === 12) { setSelMonth(1); setSelYear(y => y + 1); } else setSelMonth(m => m + 1); }}
              style={{ background: "none", border: "2px solid #0A0A0F", borderRadius: 0, padding: "3px 8px", cursor: "pointer", color: "rgba(10,10,15,0.58)", fontSize: 14 }}>›</button>
          </div>
          {/* Day headers */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2, marginBottom: 4 }}>
            {["Su","Mo","Tu","We","Th","Fr","Sa"].map((d) => (
              <div key={d} style={{ textAlign: "center", fontSize: 11, fontWeight: 600, color: "rgba(10,10,15,0.58)", padding: "4px 0", letterSpacing: 0.5, textTransform: "uppercase" }}>{d}</div>
            ))}
          </div>
          {/* Day grid */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 }}>
            {days.map((d, i) => (
              <div key={i}>
                {d ? (
                  <button
                    type="button"
                    onClick={() => selectDay(d)}
                    style={{
                      width: "100%",
                      aspectRatio: "1",
                      border: "none",
                      borderRadius: 0,
                      cursor: "pointer",
                      fontSize: 13,
                      fontFamily: "'JetBrains Mono', monospace",
                      fontWeight: d === selDay && selMonth === (parsed?.m) && selYear === (parsed?.y) ? 700 : 400,
                      background: d === selDay && selMonth === (parsed?.m) && selYear === (parsed?.y) ? "#0033FF" : "transparent",
                      color: d === selDay && selMonth === (parsed?.m) && selYear === (parsed?.y) ? "#FFFFFF" : "#0A0A0F",
                      transition: "all .1s",
                    }}
                    onMouseEnter={(e) => {
                      if (!(d === selDay && selMonth === (parsed?.m) && selYear === (parsed?.y))) {
                        e.currentTarget.style.background = "#F4F2EC";
                        e.currentTarget.style.color = "#0A0A0F";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!(d === selDay && selMonth === (parsed?.m) && selYear === (parsed?.y))) {
                        e.currentTarget.style.background = "transparent";
                        e.currentTarget.style.color = "#0A0A0F";
                      }
                    }}
                  >
                    {d}
                  </button>
                ) : <div />}
              </div>
            ))}
          </div>
          {/* Clear button */}
          {value && (
            <button type="button" onClick={clearDate}
              style={{ marginTop: 10, width: "100%", padding: "5px 0", borderRadius: 0, border: "2px solid #0A0A0F", background: "transparent", color: "rgba(10,10,15,0.58)", fontSize: 13, cursor: "pointer", fontFamily: "'Archivo', sans-serif" }}>
              Clear date
            </button>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}

function CellInput({ value, onChange, placeholder, style: s }) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{ ...S.cellInput, ...s }}
    />
  );
}

// ─── Tab navigation ──────────────────────────────────
function TabBar({ tab, setTab }) {
  const tabs = [
    { key: "registry", label: "Equipment Registry" },
    { key: "maintenance", label: "Maintenance Log" },
  ];
  return (
    <div style={{ display: "flex", gap: 4, background: "#FFFFFF", borderRadius: 0, padding: 3, border: "2px solid #0A0A0F", marginBottom: 24 }}>
      {tabs.map((t) => (
        <button
          key={t.key}
          onClick={() => setTab(t.key)}
          style={{ ...S.navBtn, ...(tab === t.key ? S.navBtnActive : {}) }}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

// ─── Equipment Registry ──────────────────────────────
function EquipmentRegistry({ equipment, setEquipment }) {
  const [editId, setEditId] = useState(null);
  const [editRow, setEditRow] = useState({});
  const [addingNew, setAddingNew] = useState(false);
  const [addCategory, setAddCategory] = useState(null);
  const [newRow, setNewRow] = useState({ name: "", make: "", model: "", serial: "", dateIntroduced: "", category: "" });
  const [collapsed, setCollapsed] = useState({});
  const [filter, setFilter] = useState("");

  const grouped = useMemo(() => {
    const groups = {};
    CATEGORIES.forEach((c) => (groups[c] = []));
    equipment.forEach((e) => {
      if (!groups[e.category]) groups[e.category] = [];
      groups[e.category].push(e);
    });
    if (filter) {
      const f = filter.toLowerCase();
      Object.keys(groups).forEach((cat) => {
        groups[cat] = groups[cat].filter(
          (e) =>
            e.name.toLowerCase().includes(f) ||
            e.make.toLowerCase().includes(f) ||
            e.model.toLowerCase().includes(f) ||
            e.serial.toLowerCase().includes(f) ||
            cat.toLowerCase().includes(f)
        );
      });
    }
    return groups;
  }, [equipment, filter]);

  const totalCount = equipment.length;

  const saveEdit = () => {
    if (!editRow.name) return;
    fetch(`${API_URL}/api/equipment/${editId}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(editRow),
    }).then((r) => r.json()).then((updated) => setEquipment(equipment.map((e) => (e.id === editId ? updated : e)))).catch(() => setEquipment(equipment.map((e) => (e.id === editId ? { ...editRow } : e))));
    setEditId(null);
  };

  const deleteRow = (id) => {
    fetch(`${API_URL}/api/equipment/${id}`, { method: "DELETE" }).catch(() => {});
    setEquipment(equipment.filter((e) => e.id !== id));
  };

  const addRow = () => {
    if (!newRow.name) return;
    fetch(`${API_URL}/api/equipment`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(newRow),
    }).then((r) => r.json()).then((created) => setEquipment([...equipment, created])).catch(() => setEquipment([...equipment, { ...newRow, id: Date.now() }]));
    setAddingNew(false);
    setAddCategory(null);
    setNewRow({ name: "", make: "", model: "", serial: "", dateIntroduced: "", category: "" });
  };

  const startAdd = (category) => {
    setAddingNew(true);
    setAddCategory(category);
    setNewRow({ name: "", make: "", model: "", serial: "", dateIntroduced: "", category });
    setCollapsed((c) => ({ ...c, [category]: false }));
  };

  const toggleCollapse = (cat) => setCollapsed((c) => ({ ...c, [cat]: !c[cat] }));

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Search equipment..."
          style={{ ...S.input, marginBottom: 0, maxWidth: 280 }}
        />
        <span style={{ color: "rgba(10,10,15,0.58)", fontSize: 15 }}>
          {totalCount} machine{totalCount !== 1 ? "s" : ""}
        </span>
      </div>

      {CATEGORIES.map((cat) => {
        const items = grouped[cat] || [];
        const isCollapsed = collapsed[cat];
        const color = CATEGORY_COLORS[cat];
        const showItems = !isCollapsed;

        return (
          <div key={cat} style={{ marginBottom: 16 }}>
            {/* Category header */}
            <button
              onClick={() => toggleCollapse(cat)}
              style={{
                ...S.monthHeader,
                borderColor: "#0A0A0F",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 4, height: 24, borderRadius: 0, background: color }} />
                <span style={{ fontSize: 16, fontWeight: 600, color: "#0A0A0F" }}>{cat}</span>
                <span style={S.jobCountBadge}>{items.length}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <button
                  onClick={(e) => { e.stopPropagation(); startAdd(cat); }}
                  style={{ ...S.addBtn, padding: "4px 10px", fontSize: 13 }}
                >
                  <PlusIcon /> Add
                </button>
                <ChevronIcon open={showItems} />
              </div>
            </button>

            {/* Table */}
            {showItems && (
              <div style={{ ...S.tableWrap, borderTop: "none", borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={{ ...S.th, width: "20%" }}>Machine Name</th>
                      <th style={{ ...S.th, width: "16%" }}>Make</th>
                      <th style={{ ...S.th, width: "16%" }}>Model</th>
                      <th style={{ ...S.th, width: "18%" }}>Serial No.</th>
                      <th style={{ ...S.th, width: "16%" }}>Date Introduced</th>
                      <th style={{ ...S.th, width: "14%", textAlign: "right" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {addingNew && addCategory === cat && (
                      <tr style={S.addingRow}>
                        <td style={S.td}><CellInput value={newRow.name} onChange={(v) => setNewRow({ ...newRow, name: v })} placeholder="Machine name" /></td>
                        <td style={S.td}><CellInput value={newRow.make} onChange={(v) => setNewRow({ ...newRow, make: v })} placeholder="Manufacturer" /></td>
                        <td style={S.td}><CellInput value={newRow.model} onChange={(v) => setNewRow({ ...newRow, model: v })} placeholder="Model #" /></td>
                        <td style={S.td}><CellInput value={newRow.serial} onChange={(v) => setNewRow({ ...newRow, serial: v })} placeholder="Serial #" /></td>
                        <td style={S.td}>
                          <DatePicker value={newRow.dateIntroduced} onChange={(v) => setNewRow({ ...newRow, dateIntroduced: v })} />
                        </td>
                        <td style={{ ...S.td, textAlign: "right" }}>
                          <button onClick={addRow} style={S.iconBtn} title="Save"><CheckIcon /></button>
                          <button onClick={() => { setAddingNew(false); setAddCategory(null); }} style={{ ...S.iconBtn, marginLeft: 6 }} title="Cancel"><XIcon /></button>
                        </td>
                      </tr>
                    )}
                    {items.map((e) => (
                      <tr key={e.id} style={editId === e.id ? S.editingRow : {}}>
                        {editId === e.id ? (
                          <>
                            <td style={S.td}><CellInput value={editRow.name} onChange={(v) => setEditRow({ ...editRow, name: v })} /></td>
                            <td style={S.td}><CellInput value={editRow.make} onChange={(v) => setEditRow({ ...editRow, make: v })} /></td>
                            <td style={S.td}><CellInput value={editRow.model} onChange={(v) => setEditRow({ ...editRow, model: v })} /></td>
                            <td style={S.td}><CellInput value={editRow.serial} onChange={(v) => setEditRow({ ...editRow, serial: v })} /></td>
                            <td style={S.td}>
                              <DatePicker value={editRow.dateIntroduced || ""} onChange={(v) => setEditRow({ ...editRow, dateIntroduced: v })} />
                            </td>
                            <td style={{ ...S.td, textAlign: "right" }}>
                              <button onClick={saveEdit} style={S.iconBtn} title="Save"><CheckIcon /></button>
                              <button onClick={() => setEditId(null)} style={{ ...S.iconBtn, marginLeft: 6 }} title="Cancel"><XIcon /></button>
                            </td>
                          </>
                        ) : (
                          <>
                            <td style={{ ...S.td, color: "#0A0A0F", fontWeight: 600 }}>{e.name}</td>
                            <td style={S.td}>{e.make}</td>
                            <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 15 }}>{e.model}</td>
                            <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 14, color: "rgba(10,10,15,0.58)" }}>{e.serial}</td>
                            <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 14, color: "#0A0A0F" }}>
                              {e.dateIntroduced ? new Date(e.dateIntroduced + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
                            </td>
                            <td style={{ ...S.td, textAlign: "right" }}>
                              <button onClick={() => { setEditId(e.id); setEditRow({ ...e }); }} style={S.iconBtn} title="Edit"><EditIcon /></button>
                              <button onClick={() => deleteRow(e.id)} style={{ ...S.iconBtn, marginLeft: 6 }} title="Delete"><TrashIcon /></button>
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                    {items.length === 0 && !(addingNew && addCategory === cat) && (
                      <tr>
                        <td colSpan={6} style={{ ...S.td, textAlign: "center", color: "rgba(10,10,15,0.58)", padding: 24 }}>
                          No machines in this category yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Maintenance Log ─────────────────────────────────
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function MaintenanceLog({ equipment, maintenanceLog, setMaintenanceLog }) {
  const [expandedMachines, setExpandedMachines] = useState({});
  const [collapsedCats, setCollapsedCats] = useState({});
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [editingCell, setEditingCell] = useState(null);
  const [cellInput, setCellInput] = useState("");

  const toggleMachine = (id) => setExpandedMachines((e) => ({ ...e, [id]: !e[id] }));
  const toggleCat = (cat) => setCollapsedCats((c) => ({ ...c, [cat]: !c[cat] }));

  // Key: "machineId-year-monthIdx-type"
  const getEntry = (machineId, monthIdx, type) => maintenanceLog[`${machineId}-${selectedYear}-${monthIdx}-${type}`] || null;

  const saveEntry = () => {
    if (!editingCell) return;
    const { machineId, monthIdx, type } = editingCell;
    const key = `${machineId}-${selectedYear}-${monthIdx}-${type}`;
    if (cellInput.trim()) {
      setMaintenanceLog({ ...maintenanceLog, [key]: cellInput.trim() });
      fetch(`${API_URL}/api/maintenance`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ key, value: cellInput.trim() }) }).catch(() => {});
    } else {
      const updated = { ...maintenanceLog };
      delete updated[key];
      setMaintenanceLog(updated);
      fetch(`${API_URL}/api/maintenance`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ key, value: "" }) }).catch(() => {});
    }
    setEditingCell(null);
    setCellInput("");
  };

  const clearEntry = () => {
    if (!editingCell) return;
    const { machineId, monthIdx, type } = editingCell;
    const key = `${machineId}-${selectedYear}-${monthIdx}-${type}`;
    const updated = { ...maintenanceLog };
    delete updated[key];
    setMaintenanceLog(updated);
    fetch(`${API_URL}/api/maintenance`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ key, value: "" }) }).catch(() => {});
    setEditingCell(null);
    setCellInput("");
  };

  const startEdit = (machineId, monthIdx, type) => {
    const entry = getEntry(machineId, monthIdx, type);
    setEditingCell({ machineId, monthIdx, type });
    setCellInput(entry || "");
  };

  const getMachineLogCount = (machineId) => {
    const prefix = `${machineId}-${selectedYear}-`;
    return Object.keys(maintenanceLog).filter((k) => k.startsWith(prefix)).length;
  };

  const totalLogs = Object.keys(maintenanceLog).filter((k) => k.includes(`-${selectedYear}-`)).length;

  const grouped = {};
  CATEGORIES.forEach((c) => (grouped[c] = []));
  equipment.forEach((e) => {
    if (!grouped[e.category]) grouped[e.category] = [];
    grouped[e.category].push(e);
  });

  return (
    <div>
      {/* Year selector + stats */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button onClick={() => setSelectedYear((y) => y - 1)} style={{ ...S.iconBtn, padding: "6px 10px", fontSize: 16 }}>‹</button>
          <span style={{ fontSize: 20, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", color: "#0A0A0F", minWidth: 60, textAlign: "center" }}>{selectedYear}</span>
          <button onClick={() => setSelectedYear((y) => y + 1)} style={{ ...S.iconBtn, padding: "6px 10px", fontSize: 16 }}>›</button>
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ color: "rgba(10,10,15,0.58)", fontSize: 14 }}>{totalLogs} log{totalLogs !== 1 ? "s" : ""} in {selectedYear}</span>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {MAINTENANCE_TYPES.map((t) => (
              <span key={t} style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, color: "rgba(10,10,15,0.58)" }}>
                <span style={{ width: 8, height: 8, borderRadius: 0, background: MAINT_TYPE_COLORS[t] }} />
                {t}
              </span>
            ))}
          </div>
        </div>
      </div>

      {CATEGORIES.map((cat) => {
        const machines = grouped[cat] || [];
        if (machines.length === 0) return null;
        const catCollapsed = collapsedCats[cat];
        const color = CATEGORY_COLORS[cat];

        return (
          <div key={cat} style={{ marginBottom: 16 }}>
            <button onClick={() => toggleCat(cat)} style={{ ...S.monthHeader, borderColor: "#0A0A0F" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 4, height: 24, borderRadius: 0, background: color }} />
                <ChevronIcon open={!catCollapsed} />
                <span style={{ fontSize: 16, fontWeight: 600, color: "#0A0A0F" }}>{cat}</span>
                <span style={S.jobCountBadge}>{machines.length}</span>
              </div>
            </button>

            {!catCollapsed && machines.map((machine) => {
              const isExpanded = expandedMachines[machine.id];
              const count = getMachineLogCount(machine.id);

              return (
                <div key={machine.id} style={{ marginLeft: 16, marginTop: 6 }}>
                  {/* Machine header */}
                  <button
                    onClick={() => toggleMachine(machine.id)}
                    style={{
                      width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center",
                      padding: "10px 16px", background: "#FFFFFF", border: "2px solid #0A0A0F",
                      borderRadius: 0, cursor: "pointer",
                      fontFamily: "'Archivo', sans-serif",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <ChevronIcon open={isExpanded} />
                      <span style={{ fontSize: 15, fontWeight: 600, color: "#0A0A0F" }}>{machine.name}</span>
                      <span style={{ fontSize: 13, color: "rgba(10,10,15,0.58)" }}>{machine.make}</span>
                    </div>
                    <span style={{
                      ...S.jobCountBadge,
                      background: count > 0 ? "#0c3a2a" : "#F4F2EC",
                      color: count > 0 ? "#34d399" : "rgba(10,10,15,0.58)",
                    }}>
                      {count} task{count !== 1 ? "s" : ""}
                    </span>
                  </button>

                  {/* Expanded: maintenance types × 12 months */}
                  {isExpanded && (
                    <div style={{ ...S.tableWrap, borderTop: "none", borderTopLeftRadius: 0, borderTopRightRadius: 0, overflowX: "auto" }}>
                      <table style={{ ...S.table, minWidth: 850 }}>
                        <thead>
                          <tr>
                            <th style={{ ...S.th, width: 140, position: "sticky", left: 0, zIndex: 2, background: "#FFFFFF" }}>Type</th>
                            {MONTHS.map((m, i) => (
                              <th key={i} style={{ ...S.th, textAlign: "center", minWidth: 56, fontSize: 11, padding: "8px 2px" }}>{m}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {MAINTENANCE_TYPES.map((type) => {
                            const typeColor = MAINT_TYPE_COLORS[type] || "rgba(10,10,15,0.58)";
                            return (
                              <tr key={type}>
                                <td style={{
                                  ...S.td, fontWeight: 500, fontSize: 13, color: typeColor,
                                  position: "sticky", left: 0, zIndex: 1, background: "#0a1930", whiteSpace: "nowrap",
                                }}>
                                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                                    <span style={{ width: 6, height: 6, borderRadius: 0, background: typeColor }} />
                                    {type}
                                  </span>
                                </td>
                                {MONTHS.map((_, monthIdx) => {
                                  const entry = getEntry(machine.id, monthIdx, type);
                                  const isEditing = editingCell && editingCell.machineId === machine.id && editingCell.monthIdx === monthIdx && editingCell.type === type;

                                  return (
                                    <td
                                      key={monthIdx}
                                      style={{ ...S.td, textAlign: "center", padding: "4px 2px", cursor: "pointer", position: "relative", minWidth: 56 }}
                                      onClick={() => { if (!isEditing) startEdit(machine.id, monthIdx, type); }}
                                    >
                                      {isEditing ? (
                                        <div
                                          style={{
                                            position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
                                            zIndex: 20, background: "#FFFFFF", border: "1px solid #0033FF", borderRadius: 0,
                                            padding: 10, minWidth: 150, boxShadow: "0 8px 24px rgba(0,0,0,.5)",
                                          }}
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <input
                                            autoFocus type="text" value={cellInput}
                                            onChange={(e) => setCellInput(e.target.value)}
                                            placeholder="e.g. 3/15"
                                            style={{ ...S.cellInput, fontSize: 13, marginBottom: 6 }}
                                            onKeyDown={(e) => { if (e.key === "Enter") saveEntry(); if (e.key === "Escape") setEditingCell(null); }}
                                          />
                                          <div style={{ display: "flex", gap: 4 }}>
                                            <button onClick={saveEntry} style={{ ...S.iconBtn, flex: 1, justifyContent: "center", borderColor: "#22c55e", color: "#22c55e" }}><CheckIcon /></button>
                                            {entry && <button onClick={clearEntry} style={{ ...S.iconBtn, flex: 1, justifyContent: "center", borderColor: "#ef4444", color: "#ef4444" }}><TrashIcon /></button>}
                                            <button onClick={() => setEditingCell(null)} style={{ ...S.iconBtn, flex: 1, justifyContent: "center" }}><XIcon /></button>
                                          </div>
                                        </div>
                                      ) : entry ? (
                                        <span style={{
                                          display: "inline-block", padding: "2px 6px", borderRadius: 0, fontSize: 12,
                                          fontFamily: "'JetBrains Mono', monospace", fontWeight: 600,
                                          background: `${typeColor}20`, color: typeColor, border: `1px solid ${typeColor}30`,
                                        }}>
                                          {entry}
                                        </span>
                                      ) : (
                                        <span style={{ color: "#0A0A0F", fontSize: 14 }}>—</span>
                                      )}
                                    </td>
                                  );
                                })}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}

      {equipment.length === 0 && (
        <div style={{ textAlign: "center", color: "rgba(10,10,15,0.58)", padding: 60 }}>
          Add equipment in the Equipment Registry tab first.
        </div>
      )}
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────
export default function EquipmentPage({ equipment, setEquipment, maintenanceLog, setMaintenanceLog }) {
  const [tab, setTab] = useState("registry");

  return (
    <div>
      <div style={{ marginBottom: 4 }}>
        <h2 style={{ fontFamily: "'Archivo Black', 'Archivo', sans-serif", fontSize: 32, fontWeight: 900, color: "#0A0A0F", letterSpacing: "-0.02em", textTransform: "uppercase", lineHeight: 1, marginBottom: 4 }}>Equipment & Maintenance</h2>
        <p style={{ fontSize: 14, color: "rgba(10,10,15,0.58)", marginBottom: 20 }}>Manage machines and track maintenance schedules</p>
      </div>
      <TabBar tab={tab} setTab={setTab} />
      {tab === "registry" ? (
        <EquipmentRegistry equipment={equipment} setEquipment={setEquipment} />
      ) : (
        <MaintenanceLog equipment={equipment} maintenanceLog={maintenanceLog} setMaintenanceLog={setMaintenanceLog} />
      )}
    </div>
  );
}
